import dayjs from 'dayjs';
import {
  COMMUNITY_INTERACTION_TYPES,
  COMMUNITY_DIRECT_LINK_URL_PREFIX,
} from '@/app/community/constants';

export const DM_PLATFORM_MAP = {
  [COMMUNITY_INTERACTION_TYPES.INSTAGRAM_CONVERSATION]: 'instagram',
  [COMMUNITY_INTERACTION_TYPES.FACEBOOK_CONVERSATION]: 'facebook',
  [COMMUNITY_INTERACTION_TYPES.TWITTER_CONVERSATION]: 'twitter',
};

const validWebsocketPlatformTypes = Object.keys(DM_PLATFORM_MAP);

export const isValidPlatformType = (type) => validWebsocketPlatformTypes.includes(type);

export const getDMPlatform = (type) => {
  if (!isValidPlatformType(type)) {
    throw new Error(
      `Invalid type, please provide a valid value from ${validWebsocketPlatformTypes}`,
    );
  }

  return DM_PLATFORM_MAP[type];
};

export const instagramConversationFormatter = (conversation) => ({
  ...conversation,
  brandParticipantId: conversation.brandParticipant?.instagramScopedId,
  followerParticipantId: conversation.followerParticipant?.instagramScopedId,
});

export const twitterConversationFormatter = (conversation) => ({
  ...conversation,
  brandParticipant: conversation.twitterBrandParticipant,
  followerParticipant: conversation.twitterFollowerParticipant,
  brandParticipantId: conversation.twitterBrandParticipant?.twitterUserId,
  followerParticipantId: conversation.twitterFollowerParticipant?.twitterUserId,
});

export const facebookConversationFormatter = (conversation) => ({
  ...conversation,
  brandParticipant: conversation.fbBrandParticipant,
  followerParticipant: conversation.fbFollowerParticipant,
  brandParticipantId: conversation.fbBrandParticipant?.facebookPsid,
  followerParticipantId: conversation.fbFollowerParticipant?.facebookPsid,
});

export const conversationFormatter = (conversation) => {
  const { type } = conversation ?? {};
  switch (type) {
    case COMMUNITY_INTERACTION_TYPES.INSTAGRAM_CONVERSATION:
      return instagramConversationFormatter(conversation);
    case COMMUNITY_INTERACTION_TYPES.FACEBOOK_CONVERSATION:
      return facebookConversationFormatter(conversation);
    case COMMUNITY_INTERACTION_TYPES.TWITTER_CONVERSATION:
      return twitterConversationFormatter(conversation);
    default:
      return {};
  }
};

export const instagramMessageFormatter = (messages) =>
  messages.map((message) => ({
    ...message,
    seenAt: message.instagramSeenAt,
    createdAt: message.instagramCreatedAt,
    instagramCreatedAt: dayjs(message.instagramCreatedAt),
  }));

export const facebookMessageFormatter = (messages) =>
  messages.map((message) => ({
    ...message,
    seenAt: message.facebookSeenAt,
    createdAt: message.facebookCreatedAt,
    facebookCreatedAt: dayjs(message.facebookCreatedAt),
  }));

export const twitterMessageFormatter = (messages) =>
  messages.map((message) => ({
    ...message,
    seenAt: message.twitterSeenAt,
    createdAt: message.twitterCreatedAt,
    twitterCreatedAt: dayjs(message.twitterCreatedAt),
  }));

export const messagesFormatter = (messages, type) => {
  switch (type) {
    case COMMUNITY_INTERACTION_TYPES.INSTAGRAM_CONVERSATION:
      return instagramMessageFormatter(messages);
    case COMMUNITY_INTERACTION_TYPES.FACEBOOK_CONVERSATION:
      return facebookMessageFormatter(messages);
    case COMMUNITY_INTERACTION_TYPES.TWITTER_CONVERSATION:
      return twitterMessageFormatter(messages);
    default:
      return {};
  }
};

export const directLinkFormatter = (conversation) => {
  const { type } = conversation;
  const prefix = COMMUNITY_DIRECT_LINK_URL_PREFIX[type];
  switch (type) {
    case COMMUNITY_INTERACTION_TYPES.INSTAGRAM_CONVERSATION:
      return conversation?.followerParticipant?.username
        ? `${prefix}${conversation.followerParticipant.username}`
        : undefined;
    case COMMUNITY_INTERACTION_TYPES.TWITTER_CONVERSATION:
      if (conversation?.brandParticipantId && conversation?.followerParticipantId) {
        return `${prefix}${conversation?.brandParticipantId}-${conversation?.followerParticipantId}`;
      }
      return undefined;
    default:
      return undefined;
  }
};
